import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import HelpPdfModal from "./HelpPdfModal";
import "./EmployeeNotice.css";
import { openAuthFile, getAuthFileObjectUrl, authFileErrorMessage } from "../lib/authFile";

// 管理側のお知らせ作成（category="company"）のみ表示する。
// イベント(event/event_cancelled)・勤怠/システム自動通知は除外する。
const SYSTEM_AUTO_NOTICE_TITLES = new Set([
  "申請を受け付けました",
  "事前申請を受け付けました",
  "出勤を記録しました",
  "退勤を記録しました",
  "イベントのお知らせ",
  "スケジュール登録のお願い",
  "打刻のお願い",
  "【申請承認のお知らせ】",
  "【申請却下のお知らせ】",
  "【申請差し戻しのお知らせ】",
  "【上司承認のお知らせ】残業の本申請をお願いします",
  "【上司却下のお知らせ】",
]);

const isCompanyNotice = (n) =>
  n && n.category === "company" && !SYSTEM_AUTO_NOTICE_TITLES.has(n.title);

const isPdf = (att) =>
  (att?.mime_type || "").toLowerCase().includes("pdf") ||
  (att?.filename || "").toLowerCase().endsWith(".pdf");

const formatSize = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const EmployeeNotice = () => {
  const navigate = useNavigate();
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [pdfView, setPdfView] = useState(null); // { title, url }

  const [replyingNotice, setReplyingNotice] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [submittingReply, setSubmittingReply] = useState(false);

  const fetchNotices = useCallback(async () => {
    setLoading(true);
    try {
      // include_read=1: 既読にしたお知らせも、管理画面で設定した表示日付までは表示する
      // （表示日付の無いお知らせは、既読にすると次回から表示されない）
      const res = await api.get("/api/notifications/", { params: { include_read: 1, _t: Date.now() } });
      const list = Array.isArray(res.data) ? res.data : (res.data?.results || []);
      setNotices(list.filter(isCompanyNotice));
    } catch (e) {
      console.error(e);
      setMessage("お知らせの取得に失敗しました。");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotices();
  }, [fetchNotices]);

  const handleMarkRead = async (id) => {
    try {
      await api.post(`/api/notifications/${id}/mark-read/`);
      setNotices((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
    } catch (e) {
      console.error(e);
    }
  };

  const handleMarkAllRead = async () => {
    const unread = notices.filter((n) => !n.is_read);
    await Promise.all(
      unread.map((n) => api.post(`/api/notifications/${n.id}/mark-read/`).catch(() => {}))
    );
    setNotices((prev) => prev.map((n) => ({ ...n, is_read: true })));
  };

  const formatDate = (dt) => {
    if (!dt) return "";
    return new Date(dt).toLocaleString("ja-JP", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // 返事送信
  const handleOpenReplyModal = (notice) => {
    setReplyingNotice(notice);
    setReplyText(notice.my_reply?.content || "");
  };
  const handleCloseReplyModal = () => {
    setReplyingNotice(null);
    setReplyText("");
  }

  const handleSubmitReplyModal = async () => {
    if (!replyText.trim()) return alert("返信内容を入力してください。");
    if (!replyingNotice) return;
    setSubmittingReply(true);

    try {
      await api.post(`/api/notifications/${replyingNotice.id}/reply/`, {
        content: replyText.trim(),
      });

      alert("返信を送信しました。");

      setNotices((prev) =>
        prev.map((n) =>
          n.id === replyingNotice.id
            ? {
                ...n,
                my_reply: {
                  content: replyText.trim(),
                  created_at: new Date().toISOString(),
                },
                is_read: true,
              }
            : n
        )
      );

      handleCloseReplyModal();
    } catch (err) {
      alert(err.response?.data?.error || "返信の送信に失敗しました。");
    } finally {
      setSubmittingReply(false);
    }
  };

  // 添付は認証付きの配信 API から取得する（/media/ の直リンクは使わない。docs/spec_file_access.md）
  const openAttachment = async (att) => {
    if (!att?.api_url) return;
    try {
      if (isPdf(att)) {
        const objectUrl = await getAuthFileObjectUrl(att.api_url);
        setPdfView({ title: att.filename || "PDF", url: objectUrl });
      } else {
        await openAuthFile(att.api_url, att.filename);
      }
    } catch (err) {
      alert(authFileErrorMessage(err));
    }
  };

  const closePdfView = () => {
    if (pdfView?.url?.startsWith("blob:")) URL.revokeObjectURL(pdfView.url);
    setPdfView(null);
  };

  const unreadCount = notices.filter((n) => !n.is_read).length;

  return (
    <div className="en-container">
      <div className="en-header">
        <button type="button" className="en-back" onClick={() => navigate(-1)} aria-label="戻る">
          ←
        </button>
        <h1 className="en-title">お知らせ</h1>
        {unreadCount > 0 && (
          <button type="button" className="en-readall" onClick={handleMarkAllRead} style={{ background: " #7dd3fc"}}>
            全て既読
          </button>
        )}
      </div>

      {message && <p className="en-error">{message}</p>}

      {loading ? (
        <p className="en-empty">読み込み中...</p>
      ) : notices.length === 0 ? (
        <p className="en-empty">お知らせはありません。</p>
      ) : (
        <div className="en-list">
          {notices.map((n) => {
            const attachments = Array.isArray(n.attachments) ? n.attachments : [];
            //const canReply = n.can_reply ?? n.allow_reply;
            return (
              <div key={n.id} className={`en-card${n.is_read ? "" : " en-card--unread"}`}>
                <div className="en-card-head">
                  {!n.is_read && <span className="en-dot" aria-label="未読" />}
                  <span className="en-card-title">{n.title}</span>
                  <span className="en-card-date">{formatDate(n.created_at)}</span>
                </div>

                {(n.content || n.message) && (
                  <div className="en-card-body">{n.content || n.message}</div>
                )}

                {attachments.length > 0 && (
                  <div className="en-attachments">
                    {attachments.map((att) => (
                      <button
                        key={att.id}
                        type="button"
                        className="en-attachment"
                        onClick={() => openAttachment(att)}
                        title={att.filename}
                      >
                        <span className="en-att-icon">{isPdf(att) ? "📄" : "📎"}</span>
                        <span className="en-att-name">{att.filename}</span>
                        {att.file_size ? (
                          <span className="en-att-size">{formatSize(att.file_size)}</span>
                        ) : null}
                      </button>
                    ))}
                  </div>
                )}
                <div className="en-card-actions">
                  {Boolean(n.can_reply) && (
                    <button
                      type="button"
                      className="en-rep-btn"
                      onClick={() => handleOpenReplyModal(n)}
                      style={{
                        background: n.my_reply ? "#e0f2fe" : "#43a047",
                        color: n.my_reply ? "#0369a1" : "#ffffff",
                        border: n.my_reply ? "1px solid #7dd3fc" : "none",
                      }}
                    >
                      {n.my_reply ? "返信済み" : "返信"}
                    </button>
                  )}

                  {!n.is_read && (
                    
                      <button type="button" className="en-read-btn" onClick={() => handleMarkRead(n.id)}>
                        既読
                      </button>
                  
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 返信モーダル */}
      {replyingNotice && (
        <div
          className="en-reply-overlay"
          onClick={handleCloseReplyModal}
        >
          <div
            className="en-reply-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: "0 0 10px 0", fontSize: "1.05em", color: "#0f172a"}}>管理者への返信</h3>
            <p style={{ margin: "0 0 12px 0", fontSize: "0.85em", color: "#64748b" }}>お知らせ対象：<strong>{replyingNotice.title}</strong></p>
            <textarea
              className="en-reply-textarea"
              rows={4}
              placeholder="返信・回答内容を入力してください…"
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap:"10px", marginTop: "16px"}}>
              <button
                type="button"
                onClick={handleCloseReplyModal}
                disabled={submittingReply}
                style={{ background: "#f1f5f9", color:"#475569", border: "1px solid #cbd5e1", 
                  padding: "7px 16px", borderRadius:"5px", fontSize: "0.85em", fontWeight: "600", cursor: "pointer" }}
              >
                閉じる
              </button>
              <button
                type="button"
                onClick={handleSubmitReplyModal}
                disabled={submittingReply}
                style={{ background: "#0284c7", color: "#ffffff", border: "none", padding: "7px 18px",
                  borderRadius:"5px", fontSize: "0.85em", fontWeight: "600", cursor: "pointer" }}
              >
                {submittingReply ? "送信中..." : "送信"}
              </button>
            </div>
          </div>
        </div>
      )};

      {pdfView && (
        <HelpPdfModal
          title={pdfView.title}
          url={pdfView.url}
          onClose={closePdfView}
        />
      )}
    </div>
  );
};

export default EmployeeNotice;
